/**
 * CONVOY TOW — the outlet.
 *
 * Drop-in replacement for `<Outlet />` in the always-mounted App shell. On the
 * one transition that tows (/marketplace → /marketplace/search) the outgoing
 * page and the incoming page are coupled on a single track: the old page slides
 * left and HAULS the new one on behind it, and the results page's cards trail
 * in afterwards as carriages, so the page arrives before its contents.
 *
 * Everywhere else it renders `{outlet}` and nothing else — no DOM, no styles,
 * no behaviour change. See usePageTow() for the gate.
 *
 * ── Why the chassis is always rendered on the framed paths ──────────────────
 * React identity is position + key. If the divs appeared only during the tow,
 * the outgoing page would find itself under a new parent at tow start and
 * REMOUNT — refetching, re-running effects, and flashing its skeletons as it
 * tows away. Keeping the chassis mounted for both marketplace paths, with the
 * page in a slot keyed by pathname, means:
 *
 *   idle      [slot /marketplace]
 *   tow start [slot /marketplace] [slot /marketplace/search]   ← key preserved,
 *                                                                no remount
 *   touchdown [slot /marketplace/search]                       ← key preserved,
 *                                                                old unmounts
 *
 * Keying by pathname (not by route id, and not including the query string) is
 * safe here precisely because these two routes are static paths: the results
 * page re-searches by changing `?params`, which must not — and does not —
 * remount it.
 */
import { Box } from '@mui/material';

import {
  incomingSlotSx,
  outgoingSlotSx,
  trackSx,
  viewportSx,
} from './page-tow-styles';
import { usePageTow } from './use-page-tow';

export default function PageTowOutlet() {
  const tow = usePageTow();

  if (!tow.framed) return tow.element;

  return (
    <Box ref={tow.viewportRef} sx={viewportSx(tow.frozenHeight)}>
      <Box sx={trackSx(tow.towing, tow.towDelay)}>
        {tow.outgoing && (
          <Box
            key={tow.outgoing.path}
            ref={tow.outgoingRef}
            aria-hidden
            sx={outgoingSlotSx(tow.scrollOffset)}
          >
            {tow.outgoing.element}
          </Box>
        )}
        <Box
          key={tow.path}
          sx={incomingSlotSx(tow.towing, tow.arriving, tow.carriageDelay)}
        >
          {tow.element}
        </Box>
      </Box>
    </Box>
  );
}
